import * as React from "react";
import { Track } from "livekit-client";
import { useLocalParticipant, usePersistentUserChoices, useRoomContext, useTrackToggle, } from "@livekit/components-react";
import { usePublishPermissions } from "./use-publish-permissions";
export function useAgentControlBar({ controls, saveUserChoices = true, onDeviceError, } = {}) {
    var _a, _b, _c, _d;
    const { microphoneTrack, localParticipant } = useLocalParticipant();
    const publishPermissions = usePublishPermissions();
    const room = useRoomContext();
    const microphoneToggle = useTrackToggle({
        source: Track.Source.Microphone,
        onDeviceError: (error) => onDeviceError === null || onDeviceError === void 0 ? void 0 : onDeviceError({ source: Track.Source.Microphone, error }),
    });
    const cameraToggle = useTrackToggle({
        source: Track.Source.Camera,
        onDeviceError: (error) => onDeviceError === null || onDeviceError === void 0 ? void 0 : onDeviceError({ source: Track.Source.Camera, error }),
    });
    const screenShareToggle = useTrackToggle({
        source: Track.Source.ScreenShare,
        onDeviceError: (error) => onDeviceError === null || onDeviceError === void 0 ? void 0 : onDeviceError({ source: Track.Source.ScreenShare, error }),
    });
    const micTrackRef = React.useMemo(() => {
        return {
            participant: localParticipant,
            source: Track.Source.Microphone,
            publication: microphoneTrack,
        };
    }, [localParticipant, microphoneTrack]);
    const visibleControls = Object.assign({ leave: true }, controls);
    (_a = visibleControls.microphone) !== null && _a !== void 0 ? _a : (visibleControls.microphone = publishPermissions.microphone);
    (_b = visibleControls.screenShare) !== null && _b !== void 0 ? _b : (visibleControls.screenShare = publishPermissions.screenShare);
    (_c = visibleControls.camera) !== null && _c !== void 0 ? _c : (visibleControls.camera = publishPermissions.camera);
    (_d = visibleControls.chat) !== null && _d !== void 0 ? _d : (visibleControls.chat = publishPermissions.data);
    const { saveAudioInputEnabled, saveAudioInputDeviceId, saveVideoInputEnabled, saveVideoInputDeviceId, } = usePersistentUserChoices({
        preventSave: !saveUserChoices,
    });
    const handleDisconnect = React.useCallback(() => {
        if (room) {
            room.disconnect();
        }
    }, [room]);
    const handleAudioDeviceChange = React.useCallback((deviceId) => {
        saveAudioInputDeviceId(deviceId !== null && deviceId !== void 0 ? deviceId : "default");
    }, [saveAudioInputDeviceId]);
    const handleVideoDeviceChange = React.useCallback((deviceId) => {
        saveVideoInputDeviceId(deviceId !== null && deviceId !== void 0 ? deviceId : "default");
    }, [saveVideoInputDeviceId]);
    const handleToggleCamera = React.useCallback(async (enabled) => {
        if (screenShareToggle.enabled) {
            screenShareToggle.toggle(false);
        }
        await cameraToggle.toggle(enabled);
        // persist video input enabled preference
        saveVideoInputEnabled(!cameraToggle.enabled);
    }, [screenShareToggle.enabled, screenShareToggle.toggle, cameraToggle.toggle]);
    const handleToggleMicrophone = React.useCallback(async (enabled) => {
        await microphoneToggle.toggle(enabled);
        // persist audio input enabled preference
        saveAudioInputEnabled(!microphoneToggle.enabled);
    }, [microphoneToggle.enabled, microphoneToggle.toggle]);
    const handleToggleScreenShare = React.useCallback(async (enabled) => {
        if (cameraToggle.enabled) {
            cameraToggle.toggle(false);
        }
        await screenShareToggle.toggle(enabled);
    }, [cameraToggle.enabled, cameraToggle.toggle, screenShareToggle.toggle]);
    return {
        micTrackRef,
        visibleControls,
        cameraToggle: Object.assign(Object.assign({}, cameraToggle), { toggle: handleToggleCamera }),
        microphoneToggle: Object.assign(Object.assign({}, microphoneToggle), { toggle: handleToggleMicrophone }),
        screenShareToggle: Object.assign(Object.assign({}, screenShareToggle), { toggle: handleToggleScreenShare }),
        handleDisconnect,
        handleAudioDeviceChange,
        handleVideoDeviceChange,
    };
}
